<template>
    <div class="rank-app">
        <!-- 添加TopBar组件 -->
        <TopBar :user="user" :apps="apps" />

        <div class="rank-header">
            <h1 class="title">🏆 排行榜</h1>
            <div class="controls">
                <!-- 数据源选择器 -->
                <div class="data-source-selector">
                    <el-select v-model="currentDataSource" @change="changeDataSource" size="small" placeholder="选择数据源">
                        <el-option label="任务价值" value="todo"></el-option>
                    </el-select>
                </div>

                <!-- 排行方式选择 -->
                <div class="rank-type-selector">
                    <el-button v-for="type in rankTypes" :key="type.value"
                        :type="currentRankType === type.value ? 'primary' : 'default'"
                        @click="changeRankType(type.value)" size="small">
                        {{ type.label }}
                    </el-button>
                </div>

                <!-- 时间切换 -->
                <div class="date-navigation" v-if="showDateNavigation">
                    <el-button @click="navigateDate(-1)" size="small">
                        <el-icon><arrow-left-bold /></el-icon>
                    </el-button>
                    <span class="current-date">{{ displayDate }}</span>
                    <el-button @click="navigateDate(1)" size="small">
                        <el-icon><arrow-right-bold /></el-icon>
                    </el-button>
                </div>

                <!-- 数据调整按钮 -->
                <!-- <el-button @click="openDataAdjustment" type="success" size="small">调整数据</el-button> -->
            </div>
        </div>

        <div class="rank-content">
            <!-- 统计信息面板 -->
            <div class="stats-panel">
                <div class="stat-item">
                    <div class="stat-value">{{ displayedRankings.length }}</div>
                    <div class="stat-label">总人数</div>
                </div>
                <div class="stat-item">
                    <div class="stat-value" v-if="currentRankType === 'todo'">
                        {{Math.max(...displayedRankings.map(item => item.right), 0)}}
                    </div>
                    <div class="stat-value" v-else>
                        {{Math.max(...displayedRankings.map(item => item.right), 0)}}
                    </div>
                    <div class="stat-label" v-if="currentRankType === 'todo'">最高价值</div>
                    <div class="stat-label" v-else>最高正确数</div>
                </div>
                <div class="stat-item">
                    <div class="stat-value" v-if="currentRankType === 'todo'">
                        {{Math.max(...displayedRankings.map(item => item.wrong), 0)}}
                    </div>
                    <div class="stat-value" v-else>
                        {{Math.max(...displayedRankings.map(item => item.wrong), 0)}}
                    </div>
                    <div class="stat-label" v-if="currentRankType === 'todo'">最低价值</div>
                    <div class="stat-label" v-else>最高错误数</div>
                </div>
                <div class="stat-item">
                    <div class="stat-value" v-if="currentRankType === 'todo'">
                        {{Math.round(displayedRankings.reduce((sum, item) => sum + item.right, 0) /
                            displayedRankings.length) || 0}}
                    </div>
                    <div class="stat-value" v-else>
                        {{Math.round(displayedRankings.reduce((sum, item) => sum + item.right, 0) /
                            displayedRankings.length) || 0}}
                    </div>
                    <div class="stat-label" v-if="currentRankType === 'todo'">平均价值</div>
                    <div class="stat-label" v-else>平均正确数</div>
                </div>
            </div>

            <transition name="fade" mode="out-in">
                <div class="rank-list" :key="currentRankType + currentDate">
                    <div v-for="(item, index) in displayedRankings" :key="item.id" class="rank-item"
                        :class="getRankClass(index)" :data-id="item.id">
                        <div class="rank-position">
                            <span class="position-number">{{ index + 1 }}</span>
                            <transition name="bounce" v-if="item.change !== 0">
                                <span class="rank-change" :class="{ 'up': item.change > 0, 'down': item.change < 0 }">
                                    {{ item.change > 0 ? '↑' : '↓' }}{{ Math.abs(item.change) }}
                                </span>
                            </transition>
                            <div class="medal" v-if="index < 3">
                                <span v-if="index === 0">🥇</span>
                                <span v-else-if="index === 1">🥈</span>
                                <span v-else-if="index === 2">🥉</span>
                            </div>
                        </div>
                        <div class="user-info">
                            <div class="avatar">
                                <el-avatar :size="40" :src="item.avatar" />
                            </div>
                            <div class="user-name">{{ item.name }}</div>
                        </div>
                        <div class="score-info">
                            <div class="score-bar">
                                <div class="right-score"
                                    :style="{ width: getScorePercentage(item.right, item.wrong) + '%' }"></div>
                            </div>
                            <div class="scores">
                                <span class="right-count">{{ item.right }}</span>
                                <span class="wrong-count">{{ item.wrong }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <!-- 排行榜图表 -->
        <div class="rank-chart" ref="chartRef"></div>

        <!-- 数据调整弹窗 -->
        <el-dialog v-model="showAdjustmentDialog" title="调整数据" width="500px">
            <el-form :model="adjustmentForm" label-width="80px">
                <el-form-item label="选择用户">
                    <el-select v-model="adjustmentForm.userId" placeholder="请选择用户" style="width: 100%">
                        <el-option v-for="user in displayedRankings" :key="user.id" :label="user.name"
                            :value="user.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="正确数">
                    <el-input-number v-model="adjustmentForm.right" :min="0" :max="1000" style="width: 100%" />
                </el-form-item>
                <el-form-item label="错误数">
                    <el-input-number v-model="adjustmentForm.wrong" :min="0" :max="1000" style="width: 100%" />
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="showAdjustmentDialog = false">取消</el-button>
                    <el-button type="primary" @click="applyDataAdjustment">确定</el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import * as echarts from 'echarts'
import { ArrowLeftBold, ArrowRightBold } from '@element-plus/icons-vue'
import TopBar from '@/components/TopBar.vue'
import AuthService from '@/services/authService'
// import UserService from '@/services/userService'
import config from '@/config'
import UserMainService from '@/services/userMainService'
import TodoService from '@/services/todoService'

export default {
    name: 'RankApp',
    components: {
        TopBar,
        ArrowLeftBold,
        ArrowRightBold
    },
    setup() {
        // 用户信息和应用列表
        const user = ref({
            name: '用户名',
            id: 1,
            userType: 1,
            avatar: require('@/assets/logo.png')
        })

        const apps = ref([
            { id: 1, name: 'OJ练习', desc: '在线编程练习', link: '/CppOJ/', icon: 'code', used: true },
            { id: 2, name: '任务清单', desc: '待办事项管理', link: '/todo', icon: 'list', used: true },
            { id: 3, name: '时间线', desc: '时间记录管理', link: '/TimeLine/', icon: 'timeline', used: false },
            { id: 4, name: '知识图谱', desc: '知识关系展示', link: '/graph', icon: 'share', used: false },
            { id: 5, name: '快速抢答', desc: '抢答与记忆训练', link: '/QuickResponse/', icon: 'quiz', used: false },
            { id: 6, name: '文件管理', desc: '文件上传下载', link: '/FileManage/', icon: 'folder', used: false },
            { id: 7, name: '数据可视化', desc: '数据图表展示', link: '/DataVisual/', icon: 'bar_chart', used: false }
        ])

        // 加载用户信息
        const loadUserInfo = () => {
            // 从本地存储获取当前用户信息
            const currentUser = AuthService.getCurrentUser();
            if (currentUser) {
                console.log('当前用户信息:', currentUser);
                user.value.name = currentUser.nickname || currentUser.username || '用户名';
                user.value.id = currentUser.userid || currentUser.id || 1;
                user.value.userType = currentUser.userType || 1;
                console.log("icon:", currentUser.icon);
                // 如果有头像信息，则更新头像路径
                if (currentUser.icon) {
                    // 规范化路径分隔符，将反斜杠转换为正斜杠
                    const normalizedIcon = currentUser.icon.replace(/\\/g, '/');
                    console.log('规范化后的icon路径:', normalizedIcon);

                    // 构建完整的后端服务器地址，确保头像从8000端口获取
                    if (normalizedIcon.startsWith('http')) {
                        console.log('完整URL:', normalizedIcon);
                        // 如果已经是完整URL，则直接使用
                        user.value.avatar = normalizedIcon;
                    } else if (normalizedIcon.startsWith('/extra-files/uploads/')) {
                        console.log('extra-files URL:', normalizedIcon);
                        // 从后端服务器的8000端口获取头像
                        user.value.avatar = `${config.API_BASE_URL}${normalizedIcon}`;
                    } else if (normalizedIcon.startsWith('uploads/')) {
                        console.log('uploads URL:', normalizedIcon);
                        // 从后端服务器的8000端口获取头像
                        //user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;

                        user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
                        //查看地址：`${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
                        console.log('user.value.avatar', user.value.avatar);
                    } else {
                        console.log('img URL:', normalizedIcon);
                        user.value.avatar = `${config.API_BASE_URL}/extra-files/uploads/${normalizedIcon}`;
                    }
                } else {
                    console.log('使用默认头像');
                    // 使用默认头像
                    user.value.avatar = require('@/assets/logo.png');
                }

                // 获取用户已订阅的应用
                loadUserApps();
            }
        }

        // 加载用户应用
        const loadUserApps = async () => {
            console.log('加载用户应用');
            try {
                // 使用现有的API获取所有应用
                const response = await UserMainService.getAllApps(user.value.userType);
                console.log('获取应用结果:', response);
                if (response.status === 200 && response.apps) {
                    console.log('获取应用数据成功:', response.apps);
                    // 更新应用列表为从API获取的应用
                    apps.value = response.apps.map(app => ({
                        id: app.id,
                        name: app.name,
                        desc: app.desc,
                        link: app.name === '任务清单' ? '/todo' : app.link,
                        // icon: getAppIcon(app.name), // 根据应用名称设置图标
                        used: app.used || false // 如果API返回了used字段则使用，否则默认为false
                    }));
                }
            } catch (error) {
                console.error('获取应用数据失败:', error);
                // 如果获取失败，使用默认应用列表
                apps.value = apps.value.map(app => ({ ...app, used: app.id === 2 })); // 假设id为2的应用是默认已订阅的
            }
        }

        // 排行类型
        const rankTypes = [
            { label: '日排行', value: 'daily' },
            { label: '周排行', value: 'weekly' },
            { label: '月排行', value: 'monthly' },
            { label: '年排行', value: 'yearly' },
            { label: '总排行', value: 'total' },

        ]

        // 当前状态
        const currentRankType = ref('daily')
        const currentDataSource = ref('todo') // 数据源，默认为任务价值
        const currentDate = ref(new Date())
        const chartRef = ref(null)
        // const amdyRef = ref(0)
        let chartInstance = null

        // 数据调整弹窗
        const showAdjustmentDialog = ref(false)
        const adjustmentForm = ref({
            userId: null,
            right: 0,
            wrong: 0
        })

        // 保存原始数据用于比较排名变化
        const originalRankings = ref({})

        // 用于动画的位置映射
        const positionMap = ref(new Map())

        // 模拟数据
        const rankingsData = ref({
            daily: [],
            weekly: [],
            monthly: [],
            yearly: [],
            total: [],
            custom: [],
            todo: []
        })

        // 初始化数据（不再生成模拟数据）
        const initializeData = () => {
            // 不再生成模拟数据，只保留原始数据保存逻辑
            originalRankings.value[currentRankType.value] = [...(rankingsData.value[currentRankType.value] || [])]
        }

        // 计算显示的排行榜数据
        const displayedRankings = computed(() => {
            // 如果当前数据源是todo，则显示todo数据
            if (currentDataSource.value === 'todo') {
                return rankingsData.value.todo || []
            }
            return rankingsData.value[currentRankType.value] || []
        })

        // 获取排名样式类
        const getRankClass = (index) => {
            if (index === 0) return 'first-place'
            if (index === 1) return 'second-place'
            if (index === 2) return 'third-place'
            return 'normal-place'
        }

        // 获取得分百分比
        const getScorePercentage = (right, wrong) => {
            const total = right + wrong
            return total > 0 ? (right / total) * 100 : 0
        }

        // 是否显示日期导航
        const showDateNavigation = computed(() => {
            return ['daily', 'weekly', 'monthly', 'yearly'].includes(currentRankType.value)
        })

        // 显示日期
        const displayDate = computed(() => {
            const date = currentDate.value
            switch (currentRankType.value) {
                case 'daily':
                    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                case 'weekly': {
                    const week = getWeekNumber(date)
                    return `${date.getFullYear()}年第${week}周`
                }
                case 'monthly':
                    return `${date.getFullYear()}年${date.getMonth() + 1}月`
                case 'yearly':
                    return `${date.getFullYear()}年`
                default:
                    return ''
            }
        })

        // 获取周数
        const getWeekNumber = (date) => {
            const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
            const pastDaysOfYear = (date - firstDayOfYear) / 86400000
            return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
        }

        // 改变排行类型
        const changeRankType = (type) => {
            // 保存当前位置信息用于动画
            saveCurrentPositions()
            currentRankType.value = type

            // 统一调用真实的API获取数据
            loadTodoRankingDataByType(type)

            // 触发动画
            nextTick(() => {
                animateRankingChanges()
            })
        }

        // 改变数据源
        const changeDataSource = (source) => {
            // 保存当前位置信息用于动画
            saveCurrentPositions()
            currentDataSource.value = source

            // 统一调用真实的API获取数据
            loadTodoRankingDataByType(currentRankType.value)

            // 触发动画
            nextTick(() => {
                animateRankingChanges()
            })
        }

        // 根据类型加载todo排行榜数据
        const loadTodoRankingDataByType = async (type) => {
            try {
                // 调用真实的API获取todo排行榜数据
                // 根据传入的类型调用不同的API
                let response;
                switch (type) {
                    case 'daily':
                        // 日排行榜
                        response = await TodoService.getWorthRankingDaily();
                        break;
                    case 'weekly':
                        // 周排行榜
                        response = await TodoService.getWorthRankingWeekly();
                        break;
                    case 'monthly':
                        // 月排行榜
                        response = await TodoService.getWorthRankingMonthly();
                        break;
                    case 'yearly':
                        // 年排行榜
                        response = await TodoService.getWorthRankingYearly();
                        break;
                    case 'total':
                        // 总排行榜
                        response = await TodoService.getWorthRankingTotal();
                        break;
                    default:
                        // 默认使用总排行榜
                        response = await TodoService.getWorthRankingTotal();
                }

                if (response.status === 'ok') {
                    // 转换数据格式以适应现有的排行榜显示
                    const users = response.ranking_data.map((item) => ({
                        id: item.id,
                        name: item.name,
                        avatar: item.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
                        right: item.worth,
                        wrong: 0, // todo排行榜不使用wrong字段
                        change: item.change || 0
                    })).sort((a, b) => b.right - a.right);

                    rankingsData.value.todo = users;
                } else {
                    console.error('获取todo排行榜数据失败:', response.message);
                    // API调用失败时，清空数据而不是使用模拟数据
                    rankingsData.value.todo = [];
                }
            } catch (error) {
                console.error('获取todo排行榜数据异常:', error);
                // API调用异常时，清空数据而不是使用模拟数据
                rankingsData.value.todo = [];
            }
        };

        // 加载todo排行榜数据
        // const loadTodoRankingData = async () => {
        //     // 使用新的函数来加载数据
        //     if (currentRankType.value === 'todo') {
        //         await loadTodoRankingDataByType(currentTodoType.value)
        //     } else {
        //         await loadTodoRankingDataByType(currentRankType.value)
        //     }
        // };

        // 日期导航
        const navigateDate = (direction) => {
            // 保存当前位置信息用于动画
            saveCurrentPositions()
            const date = new Date(currentDate.value)
            switch (currentRankType.value) {
                case 'daily':
                    date.setDate(date.getDate() + direction)
                    break
                case 'weekly':
                    date.setDate(date.getDate() + (direction * 7))
                    break
                case 'monthly':
                    date.setMonth(date.getMonth() + direction)
                    break
                case 'yearly':
                    date.setFullYear(date.getFullYear() + direction)
                    break
            }
            currentDate.value = date

            // 重新加载数据
            if (currentDataSource.value === 'todo') {
                loadTodoRankingDataByType(currentRankType.value)
            } else {
                // 对于非todo数据源，也调用真实的API而不是生成模拟数据
                loadTodoRankingDataByType(currentRankType.value)
            }
            // 触发动画
            nextTick(() => {
                animateRankingChanges()
            })
        }

        // 打开数据调整弹窗
        const openDataAdjustment = () => {
            showAdjustmentDialog.value = true
            // 默认选择第一个用户
            if (displayedRankings.value.length > 0) {
                adjustmentForm.value.userId = displayedRankings.value[0].id
                adjustmentForm.value.right = displayedRankings.value[0].right
                adjustmentForm.value.wrong = displayedRankings.value[0].wrong
            }
        }

        // 应用数据调整
        const applyDataAdjustment = () => {
            if (!adjustmentForm.value.userId) return

            // 保存当前位置信息用于动画
            saveCurrentPositions()

            // 保存调整前的排名
            const originalRankingsMap = {}
            displayedRankings.value.forEach((user, index) => {
                originalRankingsMap[user.id] = index
            })

            // 更新数据
            const userData = rankingsData.value[currentRankType.value].find(user => user.id === adjustmentForm.value.userId)
            if (userData) {
                userData.right = adjustmentForm.value.right
                userData.wrong = adjustmentForm.value.wrong

                // 重新排序
                rankingsData.value[currentRankType.value].sort((a, b) => b.right - a.right)

                // 计算排名变化
                rankingsData.value[currentRankType.value].forEach((user, newIndex) => {
                    const oldIndex = originalRankingsMap[user.id]
                    if (oldIndex !== undefined) {
                        user.change = oldIndex - newIndex
                    } else {
                        user.change = 0
                    }
                })
            }

            showAdjustmentDialog.value = false

            // 触发动画
            nextTick(() => {
                animateRankingChanges()
            })
        }

        // 初始化图表
        const initChart = () => {
            if (chartRef.value) {
                // 销毁之前的图表实例
                if (chartInstance) {
                    chartInstance.dispose()
                }

                // 创建新的图表实例
                chartInstance = echarts.init(chartRef.value)

                // 设置图表选项
                updateChart()
            }
        }

        // 更新图表
        const updateChart = () => {
            if (!chartInstance || !displayedRankings.value.length) return

            const option = {
                title: {
                    text: '排行榜趋势图',
                    left: 'center',
                    textStyle: {
                        color: '#fff'
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                legend: {
                    data: ['正确', '错误'],
                    textStyle: {
                        color: '#fff'
                    },
                    top: '10%'
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    containLabel: true
                },
                xAxis: [
                    {
                        type: 'category',
                        data: displayedRankings.value.map(item => item.name),
                        axisTick: {
                            alignWithLabel: true
                        },
                        axisLabel: {
                            color: '#fff'
                        }
                    }
                ],
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            color: '#fff'
                        }
                    }
                ],
                series: [
                    {
                        name: '正确',
                        type: 'bar',
                        barWidth: '20%',
                        data: displayedRankings.value.map(item => item.right),
                        itemStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: '#83bff6' },
                                { offset: 0.5, color: '#188df0' },
                                { offset: 1, color: '#188df0' }
                            ])
                        },
                        emphasis: {
                            itemStyle: {
                                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    { offset: 0, color: '#2378f7' },
                                    { offset: 0.7, color: '#2378f7' },
                                    { offset: 1, color: '#83bff6' }
                                ])
                            }
                        }
                    },
                    {
                        name: '错误',
                        type: 'bar',
                        barWidth: '20%',
                        data: displayedRankings.value.map(item => item.wrong),
                        itemStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: '#ffd7d7' },
                                { offset: 0.5, color: '#ff6b6b' },
                                { offset: 1, color: '#ff6b6b' }
                            ])
                        },
                        emphasis: {
                            itemStyle: {
                                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    { offset: 0, color: '#ff4b4b' },
                                    { offset: 0.7, color: '#ff4b4b' },
                                    { offset: 1, color: '#ffd7d7' }
                                ])
                            }
                        }
                    }
                ]
            }

            chartInstance.setOption(option)
        }

        // 自动更新数据
        let updateInterval = null
        const startAutoUpdate = () => {
            updateInterval = setInterval(() => {
                // 保存当前位置信息用于动画
                saveCurrentPositions()
                // 更新数据
                if (currentDataSource.value === 'todo') {
                    loadTodoRankingDataByType(currentRankType.value)
                } else {
                    initializeData()
                }
                // 触发动画
                nextTick(() => {
                    animateRankingChanges()
                })
            }, 3000) // 每3秒更新一次
        }

        const stopAutoUpdate = () => {
            if (updateInterval) {
                clearInterval(updateInterval)
                updateInterval = null
            }
        }

        // 保存当前位置信息
        const saveCurrentPositions = () => {
            const rankItems = document.querySelectorAll('.rank-item')
            rankItems.forEach((item, index) => {
                const userId = parseInt(item.dataset.id)
                const rect = item.getBoundingClientRect()
                positionMap.value.set(userId, {
                    top: rect.top,
                    left: rect.left,
                    index: index
                })
            })
        }

        // 执行排名变化动画
        const animateRankingChanges = () => {
            const rankItems = document.querySelectorAll('.rank-item')
            rankItems.forEach((item, index) => {
                const userId = parseInt(item.dataset.id)
                const oldPosition = positionMap.value.get(userId)

                if (oldPosition && oldPosition.index !== index) {
                    // 计算位置差异
                    const rect = item.getBoundingClientRect()
                    const deltaX = oldPosition.left - rect.left
                    const deltaY = oldPosition.top - rect.top

                    // 应用动画样式
                    item.style.transition = 'none'
                    item.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(0.95)`
                    item.style.opacity = '0.7'
                    item.style.zIndex = '10'
                    item.style.willChange = 'transform, opacity'

                    // 强制重绘
                    item.offsetHeight

                    // 执行动画
                    item.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)'
                    item.style.transform = 'translate(0, 0) scale(1)'
                    item.style.opacity = '1'

                    // 动画结束后清理样式
                    setTimeout(() => {
                        item.style.transition = ''
                        item.style.transform = ''
                        item.style.opacity = ''
                        item.style.zIndex = ''
                        item.style.willChange = ''
                    }, 600)
                }
            })
        }

        // 监听排行榜数据变化，更新图表
        watch(displayedRankings, () => {
            nextTick(() => {
                updateChart()
            })
        })

        // 组件挂载时初始化
        onMounted(() => {
            loadUserInfo()
            // 默认加载todo数据
            loadTodoRankingDataByType(currentRankType.value)
            startAutoUpdate()
            nextTick(() => {
                initChart()
                window.addEventListener('resize', () => {
                    if (chartInstance) {
                        chartInstance.resize()
                    }
                })
            })
        })

        // 组件卸载时清理
        onUnmounted(() => {
            stopAutoUpdate()
            if (chartInstance) {
                chartInstance.dispose()
                chartInstance = null
            }
        })

        return {
            user,
            apps,
            rankTypes,
            currentRankType,
            currentDataSource, // 新增：数据源
            currentDate,
            displayedRankings,
            showDateNavigation,
            displayDate,
            getRankClass,
            getScorePercentage,
            changeRankType,
            changeDataSource, // 新增：改变数据源的方法
            navigateDate,
            chartRef,
            // 数据调整相关
            showAdjustmentDialog,
            adjustmentForm,
            openDataAdjustment,
            applyDataAdjustment
        }
    }
}
</script>

<style scoped>
.rank-app {
    padding: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    min-height: 100vh;
    font-family: 'Arial', sans-serif;
}

.rank-header {
    text-align: center;
    margin-bottom: 30px;
}

.title {
    color: white;
    font-size: 2.5rem;
    margin-bottom: 20px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
}

.data-source-selector {
    margin-right: 10px;
}

.data-source-selector .el-select {
    width: 120px;
}

.rank-type-selector {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
    min-height: 40px;
    z-index: 10;
    position: relative;
}

.rank-type-selector .el-button {
    margin: 0;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
}

.date-navigation {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(255, 255, 255, 0.2);
    padding: 8px 15px;
    border-radius: 20px;
    backdrop-filter: blur(10px);
}

.current-date {
    color: white;
    font-weight: bold;
    min-width: 120px;
    text-align: center;
}

.rank-content {
    max-width: 800px;
    margin: 0 auto;
}

.stats-panel {
    display: flex;
    justify-content: space-between;
    gap: 15px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.stat-item {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    padding: 15px;
    text-align: center;
    flex: 1;
    min-width: 120px;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
}

.stat-item:hover {
    transform: translateY(-5px);
}

.stat-value {
    font-size: 1.8rem;
    font-weight: bold;
    color: white;
    margin-bottom: 5px;
}

.stat-label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
}

.rank-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.rank-item {
    display: flex;
    align-items: center;
    padding: 15px 20px;
    border-radius: 15px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    position: relative;
    overflow: hidden;
    /* 添加硬件加速相关样式 */
    transform: translateZ(0);
    will-change: transform, opacity;
}

.rank-item:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
}

.rank-item:hover:before {
    transform: translateX(100%);
}

.rank-item:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    z-index: 1;
}

.rank-item.first-place:hover {
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
}

.rank-item.second-place:hover {
    box-shadow: 0 6px 20px rgba(192, 192, 192, 0.4);
}

.rank-item.third-place:hover {
    box-shadow: 0 6px 20px rgba(205, 127, 50, 0.4);
}

.first-place {
    background: linear-gradient(135deg, #FFD700, #FFA500);
    border: 2px solid #FFD700;
}

.second-place {
    background: linear-gradient(135deg, #C0C0C0, #A9A9A9);
    border: 2px solid #C0C0C0;
}

.third-place {
    background: linear-gradient(135deg, #CD7F32, #A0522D);
    border: 2px solid #CD7F32;
}

.normal-place {
    background: linear-gradient(135deg, #ffffff, #f5f5f5);
}

.rank-position {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 60px;
    margin-right: 15px;
}

.position-number {
    font-size: 1.5rem;
    font-weight: bold;
    color: #333;
}

.first-place .position-number {
    color: #fff;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.second-place .position-number,
.third-place .position-number {
    color: #fff;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.rank-change {
    font-size: 0.8rem;
    font-weight: bold;
}

.rank-change.up {
    color: #4CAF50;
}

.rank-change.down {
    color: #F44336;
}

.medal {
    font-size: 1.2rem;
    margin-left: 5px;
    animation: medal-rotate 2s infinite;
}

@keyframes medal-rotate {
    0% {
        transform: rotate(0deg);
    }

    25% {
        transform: rotate(10deg);
    }

    50% {
        transform: rotate(0deg);
    }

    75% {
        transform: rotate(-10deg);
    }

    100% {
        transform: rotate(0deg);
    }
}

.user-info {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 150px;
}

.avatar {
    margin-right: 15px;
}

.user-name {
    font-weight: bold;
    color: #333;
    font-size: 1.1rem;
}

.first-place .user-name,
.second-place .user-name,
.third-place .user-name {
    color: #fff;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.score-info {
    flex: 2;
    min-width: 200px;
}

.score-bar {
    height: 12px;
    background: #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 8px;
    position: relative;
}

.right-score {
    height: 100%;
    background: linear-gradient(90deg, #4CAF50, #8BC34A);
    border-radius: 6px;
    transition: width 1s ease-in-out;
}

.scores {
    display: flex;
    justify-content: space-between;
    font-weight: bold;
}

.right-count {
    color: #4CAF50;
}

.wrong-count {
    color: #F44336;
}

/* 图表样式 */
.rank-chart {
    height: 400px;
    margin-top: 30px;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
}

/* 动画效果 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.bounce-enter-active {
    animation: bounce-in 0.5s;
}

.bounce-leave-active {
    animation: bounce-in 0.5s reverse;
}

@keyframes bounce-in {
    0% {
        transform: scale(0);
    }

    50% {
        transform: scale(1.2);
    }

    100% {
        transform: scale(1);
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .rank-app {
        padding: 10px;
    }

    .controls {
        flex-direction: column;
        gap: 15px;
    }

    .rank-item {
        flex-direction: column;
        align-items: flex-start;
    }

    .rank-position {
        flex-direction: row;
        width: auto;
        margin-right: 0;
        margin-bottom: 10px;
    }

    .user-info {
        margin-bottom: 10px;
        width: 100%;
    }

    .score-info {
        width: 100%;
    }

    .rank-chart {
        height: 300px;
    }

    .stats-panel {
        flex-direction: column;
    }
}
</style>